import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Select, Space, Spin, Table, Typography, message } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const { Text } = Typography;
const money = value => Number(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AccountingPostingPreviewModal({ open, documentType, documentId, onClose, onPosted }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); const [posting, setPosting] = useState(false);
  const [accounts, setAccounts] = useState([]); const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState(null); const [overrides, setOverrides] = useState({}); const [preview, setPreview] = useState(null);
  const accountOptions = useMemo(() => accounts.map(a => ({ value: Number(a.id), label: `${a.code || a.CODE} - ${a.name || a.NAME}` })), [accounts]);

  const loadPreview = async (nextPreset = presetId, nextOverrides = overrides) => {
    if (!documentId) return;
    setLoading(true);
    try {
      const params = { ...(nextPreset ? { preset_id: nextPreset } : {}), ...(Object.keys(nextOverrides).length ? { account_overrides: JSON.stringify(nextOverrides) } : {}) };
      const response = await api.get(`/api/accounting/${documentType}s/${documentId}/preview`, { params });
      setPreview(response.data.data);
      if (!nextPreset && response.data.data?.preset_id) setPresetId(Number(response.data.data.preset_id));
    } catch (error) { message.error(error.response?.data?.message || 'ไม่สามารถเตรียมรายการบัญชีได้'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    setPresetId(null); setOverrides({}); setPreview(null); setLoading(true);
    Promise.all([api.get('/api/accounting/accounts'), api.get('/api/accounting/posting-presets')])
      .then(([accountResponse, presetResponse]) => {
        const raw = accountResponse.data.data || accountResponse.data || [];
        setAccounts(Array.isArray(raw) ? raw : (raw.accounts || []));
        setPresets(presetResponse.data.data || []);
        return loadPreview(null, {});
      }).finally(() => setLoading(false));
  }, [open, documentId, documentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectPreset = async value => { setPresetId(value || null); setOverrides({}); await loadPreview(value || null, {}); };
  const selectAccount = (role, accountId) => {
    const next = { ...overrides, [role]: accountId }; setOverrides(next);
    const selected = accounts.find(a => Number(a.id) === Number(accountId));
    setPreview(current => ({ ...current, entry: { ...current.entry, items: current.entry.items.map(item => item.role === role ? { ...item, account_id: accountId, code: selected?.code || selected?.CODE, name: selected?.name || selected?.NAME } : item) } }));
  };
  const post = async () => {
    setPosting(true);
    try {
      const response = await api.post(`/api/accounting/${documentType}s/${documentId}/post`, { preset_id: presetId, account_overrides: overrides });
      message.success(`ลงบัญชีสำเร็จ เลขที่ ${response.data.data.jv_number}`); onClose(); onPosted?.();
    } catch (error) { message.error(error.response?.data?.message || 'ลงบัญชีไม่สำเร็จ'); }
    finally { setPosting(false); }
  };
  const balanced = Number(preview?.entry?.total_debit || 0).toFixed(2) === Number(preview?.entry?.total_credit || 0).toFixed(2);
  return <Modal open={open} onCancel={onClose} title={`ตรวจสอบรายการบัญชี ${preview?.entry?.reference_no || ''}`} width={900}
    footer={[<Button key="settings" icon={<SettingOutlined />} onClick={() => navigate('/accounting/settings')}>ตั้งค่า Preset</Button>,<Button key="cancel" onClick={onClose}>ยกเลิก</Button>,<Button key="post" type="primary" loading={posting} disabled={!preview || !balanced} onClick={post}>ยืนยันลงบัญชี</Button>]}>
    <Spin spinning={loading}><Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div><Text strong>Preset งาน: </Text><Select allowClear placeholder="ใช้ค่ากลางของระบบ" value={presetId} onChange={selectPreset} style={{ width: 320 }} options={presets.map(p => ({ value: p.id, label: p.name }))} /></div>
      <Alert type={balanced ? 'success' : 'error'} showIcon message={`เดบิต ${money(preview?.entry?.total_debit)} บาท / เครดิต ${money(preview?.entry?.total_credit)} บาท`} description="เลือกผังบัญชีได้ แต่ยอดเงินถูกล็อกจากเอกสารต้นฉบับ" />
      <Table size="small" pagination={false} rowKey={item => item.role} dataSource={preview?.entry?.items || []} columns={[
        { title: 'ผังบัญชี', width: 320, render: (_, item) => <Select showSearch optionFilterProp="label" style={{ width: '100%' }} value={Number(item.account_id)} options={accountOptions} onChange={value => selectAccount(item.role, value)} /> },
        { title: 'คำอธิบาย', dataIndex: 'description' }, { title: 'เดบิต', dataIndex: 'debit', align: 'right', width: 120, render: money }, { title: 'เครดิต', dataIndex: 'credit', align: 'right', width: 120, render: money }
      ]} />
    </Space></Spin>
  </Modal>;
}
